import React from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Package, User, AlertTriangle } from 'lucide-react';

const ITEM_TYPE = 'KANBAN_CARD';

const KanbanCard = ({ order, onDragEnd, column }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ITEM_TYPE,
    item: { order, fromColumn: column },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [order, column]);

  const priorityColors = {
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-green-50 text-green-700 border-green-200',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const isOverdue = order.expected_delivery_date && new Date(order.expected_delivery_date) < new Date();

  return (
    <div
      ref={drag}
      className={`bg-white rounded-lg border border-slate-200 p-3 mb-2 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-all ${
        isDragging ? 'opacity-50 scale-105' : ''
      } ${isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-sm text-slate-900 truncate">{order.line_items?.[0]?.item_name || order.customer_name}</span>
        <Badge variant="outline" className={`text-[10px] ${priorityColors[order.priority] || priorityColors.medium}`}>
          {order.priority?.toUpperCase()}
        </Badge>
      </div>
      {order.firm_name && <p className="text-[10px] text-slate-400 truncate mb-1">{order.firm_name}</p>}
      <p className="text-xs text-slate-500 truncate">{order.customer_name}</p>
      <p className="text-[10px] text-slate-400 mb-2">{order.order_id}</p>
      
      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <div className="flex items-center gap-1">
          <Package className="h-3 w-3" />
          <span>{order.line_items?.length || 0} items</span>
        </div>
        <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
          {isOverdue && <AlertTriangle className="h-3 w-3" />}
          <Calendar className="h-3 w-3" />
          <span>{formatDate(order.expected_delivery_date)}</span>
        </div>
      </div>

      {order.assigned_designer_name && (
        <div className="flex items-center gap-1 mt-2 pt-2 border-t text-[10px] text-slate-500">
          <User className="h-3 w-3" />
          <span>{order.assigned_designer_name}</span>
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({ title, column, orders, onDrop, color = 'slate' }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item) => {
      if (item.fromColumn !== column) {
        onDrop(item.order, item.fromColumn, column);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }), [column, onDrop]);

  const colorStyles = {
    slate: 'bg-slate-100 border-slate-200',
    blue: 'bg-blue-50 border-blue-200',
    amber: 'bg-amber-50 border-amber-200',
    green: 'bg-green-50 border-green-200',
  };

  const headerColors = {
    slate: 'text-slate-700',
    blue: 'text-brand-blue',
    amber: 'text-amber-700',
    green: 'text-green-700',
  };

  return (
    <div
      ref={drop}
      className={`flex-1 min-w-[280px] max-w-[320px] rounded-xl border p-3 transition-all ${
        colorStyles[color]
      } ${isOver ? 'ring-2 ring-brand-blue ring-opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-heading font-semibold text-sm ${headerColors[color]}`}>{title}</h3>
        <Badge variant="secondary" className="text-[10px] bg-white/80">
          {orders.length}
        </Badge>
      </div>
      <div className="space-y-2 min-h-[200px] max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
        {orders.map((order) => (
          <KanbanCard key={order.id} order={order} column={column} />
        ))}
        {orders.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-xs">
            No orders in this column
          </div>
        )}
      </div>
    </div>
  );
};

export default function KanbanBoard({ columns, onColumnChange }) {
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => (
          <KanbanColumn
            key={col.id}
            title={col.title}
            column={col.id}
            orders={col.orders}
            onDrop={onColumnChange}
            color={col.color}
          />
        ))}
      </div>
    </DndProvider>
  );
}
